import {
  App,
  Button,
  Empty,
  Flex,
  Switch,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Eye, Pencil, Plus, Trash2, Zap } from 'lucide-react'
import { useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type { WorkspaceApp } from 'src/api/facade/types'
import type { OutboundWebhookRead } from 'src/api/main'
import {
  DateTimeCell,
  MonoValue,
  ProTableContainer,
  TableActionColumnTitle
} from 'src/components'
import { useWorkspaceRoute } from 'src/features/workspaces/pages/WorkspacePageShell'
import { paths } from 'src/router/paths'
import { useShowApiErrors } from 'src/utils'

import {
  useEventCatalogue,
  useOutboundWebhookList,
  useOutboundWebhookMutations,
  useSquadAgentUrl,
  useWebhookTest
} from './hooks'
import { SigningSecretModal } from './SigningSecretModal'
import { WebhookFormDrawer, type WebhookFormValues } from './WebhookFormDrawer'
import { WebhookName } from './WebhookName'
import { WebhookTestResultModal } from './WebhookTestResultModal'

const { Text } = Typography

type Props = {
  app: WorkspaceApp
}

type DrawerMode =
  | { kind: 'create' }
  | { kind: 'edit'; webhook: OutboundWebhookRead }
  | null

export const AppOutboundWebhooksPanel = ({ app }: Props) => {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const navigate = useNavigate()
  const { id: workspaceIdStr } = useWorkspaceRoute()

  const applicationId = Number(app.id) || 0
  const list = useOutboundWebhookList(applicationId)
  const { create, update, remove, reveal } =
    useOutboundWebhookMutations(applicationId)
  const test = useWebhookTest(applicationId)
  const squadAgentUrlFor = useSquadAgentUrl(app.workspaceId)

  const [drawer, setDrawer] = useState<DrawerMode>(null)
  const [revealed, setRevealed] = useState<string | null>(null)

  // Only fetched while the form is open: the catalogue is a second service, so a request on
  // every visit to this section would make the list depend on CRM being up.
  const catalogue = useEventCatalogue({
    domain: app.domain,
    appToken: app.token,
    enabled: drawer !== null
  })

  const webhooks = list.data ?? []

  // A single mutation object backs every row, so `isPending` alone would spin the control on all
  // of them. React Query keeps the in-flight variables, which is what identifies the row.
  const pendingFor = (
    mutation: { isPending: boolean; variables?: { path: { id: number } } },
    id: number
  ) => mutation.isPending && mutation.variables?.path.id === id

  const handleSubmit = async (values: WebhookFormValues) => {
    const body = {
      name: values.name ?? '',
      url: values.url,
      events: values.events,
      is_active: values.is_active,
      // Omitted when empty so an edit never overwrites the stored secret with a blank.
      ...(values.custom_secret ? { custom_secret: values.custom_secret } : {})
    }

    try {
      // Both paths answer with the saved record, which is what the detail route needs -- on
      // create it is the only place the new id exists.
      const saved =
        drawer?.kind === 'edit'
          ? await update.mutateAsync({
              path: { application_id: applicationId, id: drawer.webhook.id },
              body
            })
          : await create.mutateAsync({
              path: { application_id: applicationId },
              body
            })
      setDrawer(null)
      // Straight to the record that was just saved: the list can only show a count of events,
      // so "did it store what I typed" is a question only this page answers.
      navigate(
        paths.workspaces.apps.outboundWebhookDetail(
          workspaceIdStr,
          app.id,
          String(saved.id)
        )
      )
    } catch (error) {
      showApiErrors(error)
    }
  }

  const handleToggle = async (
    webhook: OutboundWebhookRead,
    active: boolean
  ) => {
    try {
      await update.mutateAsync({
        path: { application_id: applicationId, id: webhook.id },
        body: { is_active: active }
      })
    } catch (error) {
      showApiErrors(error)
    }
  }

  // Centered rather than a Popconfirm: the actions column is fixed to the right edge, so the
  // popup had nowhere to open and was clipped by the viewport along with its Confirm button.
  // The url in the body does what the anchor used to -- says which row this is about.
  const handleDelete = (webhook: OutboundWebhookRead) => {
    modal.confirm({
      title: t(
        'workspace.apps.outboundWebhooks.deleteModal.title',
        'Delete webhook?'
      ),
      content: (
        // align to the start, or the chip stretches to the dialog width and reads as a disabled
        // input rather than as the name of the thing being deleted.
        <Flex vertical gap={8} align="flex-start">
          <MonoValue ellipsis copyable={false}>
            {webhook.url}
          </MonoValue>
          <Text type="secondary">
            {t(
              'workspace.apps.outboundWebhooks.deleteModal.content',
              'Deliveries stop immediately, including attempts already queued for retry.'
            )}
          </Text>
        </Flex>
      ),
      okText: t('button.delete', 'Delete'),
      cancelText: t('cancel', 'Cancel'),
      okButtonProps: { danger: true },
      onOk: () => performDelete(webhook)
    })
  }

  const performDelete = async (webhook: OutboundWebhookRead) => {
    try {
      await remove.mutateAsync({
        path: { application_id: applicationId, id: webhook.id }
      })
      message.success(
        t('workspace.apps.outboundWebhooks.messages.deleted', 'Webhook deleted')
      )
    } catch (error) {
      showApiErrors(error)
    }
  }

  const handleReveal = async (webhook: OutboundWebhookRead) => {
    try {
      const result = await reveal.mutateAsync({
        path: { application_id: applicationId, id: webhook.id }
      })
      setRevealed(result.secret)
    } catch (error) {
      showApiErrors(error)
    }
  }

  // Not reusing useCredentialRowClick: it constrains the record to a string id, and a webhook id
  // is numeric. Same guard, so a click on an action button does not also open the row.
  const rowClick = (record: OutboundWebhookRead) => ({
    style: { cursor: 'pointer' },
    onClick: (event: MouseEvent<HTMLElement>) => {
      if (
        (event.target as HTMLElement).closest(
          'a, button, [role="button"], [role="switch"], .ant-popover'
        )
      ) {
        return
      }
      navigate(
        paths.workspaces.apps.outboundWebhookDetail(
          workspaceIdStr,
          app.id,
          String(record.id)
        )
      )
    }
  })

  const columns: ColumnsType<OutboundWebhookRead> = [
    {
      title: t('workspace.apps.outboundWebhooks.columns.name', 'Name'),
      key: 'name',
      fixed: 'left',
      width: 240,
      render: (_, item) => (
        <WebhookName webhook={item} squadAgentUrl={squadAgentUrlFor(item)} />
      )
    },
    {
      title: t('workspace.apps.outboundWebhooks.columns.url', 'Endpoint'),
      key: 'url',
      render: (_, item) => <MonoValue ellipsis>{item.url}</MonoValue>
    },
    {
      title: t('workspace.apps.outboundWebhooks.columns.events', 'Events'),
      key: 'events',
      width: 110,
      render: (_, item) => (
        <Tooltip title={item.events.join(', ')}>
          <Tag>
            {t('workspace.apps.outboundWebhooks.columns.eventCount', {
              count: item.events.length,
              defaultValue: '{{count}} events'
            })}
          </Tag>
        </Tooltip>
      )
    },
    {
      title: t('workspace.apps.outboundWebhooks.columns.active', 'Active'),
      key: 'is_active',
      width: 80,
      render: (_, item) => (
        <Switch
          size="small"
          checked={item.is_active}
          loading={pendingFor(update, item.id)}
          onChange={(checked) => handleToggle(item, checked)}
        />
      )
    },
    {
      title: t('workspace.apps.outboundWebhooks.columns.created', 'Created'),
      key: 'date_created',
      // 170 clipped a two-digit hour with minutes -- "Aug 18, 2026, 11:11 AM" is the widest this
      // format gets, and it wrapped onto a second line.
      width: 190,
      render: (_, item) => (
        <DateTimeCell
          value={item.date_created}
          variant="dateTime"
          style={{ whiteSpace: 'nowrap' }}
        />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 150,
      align: 'center',
      fixed: 'right',
      render: (_, item) => (
        <Flex gap={4} justify="center">
          <Tooltip
            title={t(
              'workspace.apps.outboundWebhooks.actions.test',
              'Send test webhook'
            )}
          >
            <Button
              type="text"
              size="small"
              icon={<Zap size={14} />}
              loading={pendingFor(test.mutation, item.id)}
              onClick={() => test.send(item.id)}
            />
          </Tooltip>
          <Tooltip
            title={t(
              'workspace.apps.outboundWebhooks.actions.reveal',
              'Show signing secret'
            )}
          >
            <Button
              type="text"
              size="small"
              icon={<Eye size={14} />}
              loading={pendingFor(reveal, item.id)}
              onClick={() => handleReveal(item)}
            />
          </Tooltip>
          <Tooltip title={t('button.edit', 'Edit')}>
            <Button
              type="text"
              size="small"
              icon={<Pencil size={14} />}
              onClick={() => setDrawer({ kind: 'edit', webhook: item })}
            />
          </Tooltip>
          <Tooltip title={t('button.delete', 'Delete')}>
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 size={14} />}
              loading={pendingFor(remove, item.id)}
              onClick={() => handleDelete(item)}
            />
          </Tooltip>
        </Flex>
      )
    }
  ]

  return (
    <Flex vertical gap={14}>
      <ProTableContainer<OutboundWebhookRead>
        showSearch={false}
        tableIdentifier="app-outbound-webhooks"
        tableLayout="fixed"
        rowKey="id"
        loading={list.isLoading}
        dataSource={webhooks}
        columns={columns}
        onRow={rowClick}
        pagination={false}
        scroll={{ x: 1140 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t(
                    'workspace.apps.outboundWebhooks.empty',
                    'No endpoints yet. Add one to start receiving CRM events.'
                  )}
                </Text>
              }
            />
          )
        }}
        leftChildren={
          <span>
            <strong>{webhooks.length}</strong>{' '}
            <Text type="secondary">
              {t(
                'workspace.apps.outboundWebhooks.toolbar.label',
                'configured endpoints'
              )}
            </Text>
          </span>
        }
        rightChildren={
          <Button
            type="primary"
            size="small"
            icon={<Plus size={14} />}
            onClick={() => setDrawer({ kind: 'create' })}
          >
            {t('workspace.apps.outboundWebhooks.create', 'Add webhook')}
          </Button>
        }
      />

      <WebhookFormDrawer
        open={drawer !== null}
        webhook={drawer?.kind === 'edit' ? drawer.webhook : null}
        catalogue={{
          groups: catalogue.data,
          isLoading: catalogue.isLoading,
          isError: catalogue.isError
        }}
        submitting={create.isPending || update.isPending}
        onSubmit={handleSubmit}
        onClose={() => setDrawer(null)}
      />

      <SigningSecretModal secret={revealed} onClose={() => setRevealed(null)} />

      <WebhookTestResultModal result={test.result} onClose={test.close} />
    </Flex>
  )
}
